import type { MatDiscoveryResponse, MatDiscoveryItem } from '../../types/mat-types';
import type { ColDef } from 'ag-grid-community';
import type { GetRowClassParams } from './MATGrid.types';

const MAT_GRID_EXPAND_COLUMN_WIDTH = 45;
const MAT_GRID_EXPAND_COLUMN_FIELD = '__expand__';
const MAT_GRID_VENDOR_TICKET_COLUMN_FIELD = '__vendorTicket__';
const MAT_GRID_VENDOR_TICKET_COLUMN_WIDTH = 35;

/**
 * Fetches all paginated discovered data from the MAT discovery endpoint
 * Makes multiple API calls to retrieve all results since the endpoint doesn't support filtering server-side
 */
export async function fetchAllDiscoveredData(
  matDiscoveryFn: (params: any) => Promise<MatDiscoveryResponse>,
  includeReviewedOnly?: boolean
): Promise<MatDiscoveryResponse> {
  let allResults: MatDiscoveryItem[] = [];
  let after: string | undefined;
  const limit = 150;
  let hasMore = true;

  while (hasMore) {
    const response = await matDiscoveryFn({
      limit,
      has_existing_mat: 'false',
      ...(includeReviewedOnly ? { data_verified: 'true' } : {}),
      ...(after !== undefined ? { after } : {}),
    });

    if (response.results && response.results.length > 0) {
      allResults.push(...response.results);
      after = response.results[response.results.length - 1].mat_key;

      // Check if there are more results
      hasMore = response.next !== null;
    } else {
      hasMore = false;
    }
  }

  // Return combined response
  return {
    count: allResults.length,
    next: null,
    previous: null,
    results: allResults,
  };
}

/**
 * Creates the expand column definition for the grid
 */
export function createExpandColumnDef(
  handleExpandRow: (data: any) => void,
  isExpanded: (data: any) => boolean,
  cellRenderer: any
): ColDef {
  return {
    field: MAT_GRID_EXPAND_COLUMN_FIELD,
    headerName: '',
    width: MAT_GRID_EXPAND_COLUMN_WIDTH,
    minWidth: MAT_GRID_EXPAND_COLUMN_WIDTH,
    maxWidth: MAT_GRID_EXPAND_COLUMN_WIDTH,
    resizable: false,
    sortable: false,
    filter: false,
    floatingFilter: false,
    pinned: 'left' as const,
    cellRenderer,
    cellRendererParams: (params: any) => ({
      onExpandRow: handleExpandRow,
      isExpanded: isExpanded(params.data),
    }),
    suppressHeaderMenuButton: true,
    lockPosition: true,
    cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  };
}

/**
 * Creates the vendor ticket icon column definition for the grid.
 * Pinned left, shows a small icon on discovered rows with a normalized vendor name.
 */
export function createVendorTicketColumnDef(cellRenderer: any): ColDef {
  return {
    field: MAT_GRID_VENDOR_TICKET_COLUMN_FIELD,
    headerName: '',
    width: MAT_GRID_VENDOR_TICKET_COLUMN_WIDTH,
    minWidth: MAT_GRID_VENDOR_TICKET_COLUMN_WIDTH,
    maxWidth: MAT_GRID_VENDOR_TICKET_COLUMN_WIDTH,
    resizable: false,
    sortable: false,
    filter: false,
    floatingFilter: false,
    pinned: 'left' as const,
    cellRenderer,
    suppressHeaderMenuButton: true,
    lockPosition: true,
    cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
  };
}

/**
 * Determines the CSS classes for a grid row based on its state
 */
export function getRowClasses(
  params: GetRowClassParams,
  selectedMatKey: string | null | undefined,
  allMatKeys: Set<string>
): string {
  const classes: string[] = [];

  // Highlight the row if it's currently selected/expanded
  if (selectedMatKey && params.data?.MatKey === selectedMatKey) {
    classes.push('ag-row-selected-expanded');
  }

  // Green background for discovered rows
  if (params.data?.__isDiscovered__) {
    classes.push('ag-row-discovered');
  }

  // Highlight if MatKey has discovered data (existing functionality)
  if (params.data?.MatKey && allMatKeys.has(params.data.MatKey)) {
    classes.push('ag-row-has-discovery');
  }

  return classes.join(' ');
}
